import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { UserOptionType } from 'src/api/types';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import _ from 'lodash';
import { TableUserItem } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { WorkspaceQuery, useRemovePeopleMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';

type UseColumns = (a: {
  workspace: NonNullable<WorkspaceQuery['workspace']>;
  defaultColumns?: string[];
  onInviteClick: (a: { text: string; ownerId: string; email: string }) => void;
}) => {
  columns: ColumnDef<UserOptionType>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

export const usePlaceholdersColumns: UseColumns = ({
  defaultColumns = ['text', 'email'],
  onInviteClick,
  workspace,
}) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.wsPeople}-columns`, defaultColumns);
  const { refetchQuery } = useCache();

  const [removePerson] = useRemovePeopleMutation({ onCompleted: () => refetchQuery({ include: ['profile'] }) });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<UserOptionType>[] = [
      {
        id: 'text',
        header: 'Name',
        accessorFn: (row) => row.text,
        cell: ({ row }) => {
          return <TableUserItem name={row.original.text || ''} />;
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'email',
        header: 'Email',
        accessorFn: (row) => row.text,
        cell: ({ row }) => row.original.email,
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const canInvite = workspace.actionsOnThisWorkspace?.canInviteUsers;

          const handleOnInviteClick = canInvite
            ? () =>
                onInviteClick({
                  ownerId: row.original.id!,
                  text: row.original.text || '',
                  email: row.original.email || '',
                })
            : undefined;

          const handleOnRemoveClick = canInvite
            ? () => {
                const compoundId = `${workspace.id}:${row.original.text}:${row.original.email}`;
                removePerson({ variables: { id: compoundId } });
              }
            : undefined;

          return (
            <div css="display: flex; grid-gap: 8px">
              <GTTdm onInviteClick={handleOnInviteClick} onRemoveUserClick={handleOnRemoveClick} />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [workspace, selectedColumns]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.wsPeople}
      options={[{ key: 'text', title: 'Name' }]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
